import { first } from 'lodash';

import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  mapBundleToForm,
  NEW_BUNDLE_ID_SENTINEL,
  NEW_PAGE_ID_SENTINEL,
  remapBundle,
  remapPage,
} from '../ClientPresentationDesigner.utils';
import { isSinglePageBundle } from '../components/ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import {
  ClientPresentationV2Bundle,
  ClientPresentationV2EntitySummaryBundleConfiguration,
} from '../types/ClientPresentation.PresentationBundleType';
import {
  ReconcileSaveArgs,
  ReconcileSaveResult,
} from './presentationSave.types';

function getPageReconciliationError(
  formBundle: ClientPresentationV2Bundle,
  responseBundle: ClientPresentationV2Bundle
): string | null {
  if (responseBundle.pages.length !== formBundle.pages.length) {
    return `response page count (${responseBundle.pages.length}) does not match form page count (${formBundle.pages.length}) for bundle ${formBundle.id}`;
  }

  const isNewBundle = formBundle.id.startsWith(NEW_BUNDLE_ID_SENTINEL);
  for (let index = 0; index < formBundle.pages.length; index++) {
    const formPage = formBundle.pages[index]!;
    const responsePage = responseBundle.pages[index]!;

    if (responsePage.type !== formPage.type) {
      return `response page kind at index ${index} does not match form page kind for bundle ${formBundle.id}`;
    }

    if (
      !isNewBundle &&
      !formPage.id.startsWith(NEW_PAGE_ID_SENTINEL) &&
      responsePage.id !== formPage.id
    ) {
      return `response page at index ${index} does not match form page ${formPage.id}`;
    }
  }

  return null;
}

function entitySummaryConfigurationsMatch(
  form: ClientPresentationV2EntitySummaryBundleConfiguration,
  response: ClientPresentationV2EntitySummaryBundleConfiguration
): boolean {
  return (
    form.entityId === response.entityId &&
    form.deathOrderId === response.deathOrderId &&
    form.includeEntitySummaryPage === response.includeEntitySummaryPage &&
    form.includeBeneficiariesAndDispositionsPage ===
      response.includeBeneficiariesAndDispositionsPage &&
    form.includeDiagramAndNotesPage === response.includeDiagramAndNotesPage &&
    form.includeBalanceSheetPage === response.includeBalanceSheetPage &&
    form.includeBalanceSheetExportPage ===
      response.includeBalanceSheetExportPage &&
    form.includeEntityNotesOnSummaryPage ===
      response.includeEntityNotesOnSummaryPage &&
    form.includeInsurancePolicySlides === response.includeInsurancePolicySlides
  );
}

export function preserveCurrentEstateInsights(
  formBundle: ClientPresentationV2Bundle,
  reconciledBundle: ClientPresentationV2Bundle
): ClientPresentationV2Bundle {
  const currentInsights =
    formBundle.estateInsightsBundleConfiguration?.estateInsights;
  if (!currentInsights || !reconciledBundle.estateInsightsBundleConfiguration) {
    return reconciledBundle;
  }

  return {
    ...reconciledBundle,
    estateInsightsBundleConfiguration: {
      ...reconciledBundle.estateInsightsBundleConfiguration,
      // Presentation saves do not change household insight IDs. Keep this
      // array unchanged so the positional ID merge in mergeReconciledBundles
      // cannot assign another insight's ID when the response order differs.
      estateInsights: currentInsights,
    },
  };
}

/** Reconciles post-save IDs without planning or building another save. */
export function reconcilePresentation({
  bundles,
  selectedItemId,
  responseData,
  context,
}: ReconcileSaveArgs): ReconcileSaveResult {
  const { householdId, presentationId } = context;
  const responseBundles = getNodes(
    first(getNodes(responseData.clientPresentationV2s))?.bundles
  );
  if (responseBundles.length !== bundles.length) {
    const reason = `response bundle count (${responseBundles.length}) does not match form bundle count (${bundles.length})`;
    diagnostics.warn(`syncBundlesFromResponse: ${reason}`);
    return { kind: 'desynced', reason };
  }

  let needsIDSync = false;
  let reconciliationError: string | null = null;
  const updatedBundles = bundles.map((formBundle, index) => {
    const responseBundle = responseBundles[index];
    if (!responseBundle || responseBundle.kind !== formBundle.type) {
      reconciliationError = `response bundle at index ${index} does not match form bundle ${formBundle.id}`;
      return formBundle;
    }

    const mapped = mapBundleToForm(responseBundle, householdId, presentationId);
    if (!mapped) {
      reconciliationError = `response bundle ${responseBundle.id} could not be mapped to form state`;
      return formBundle;
    }

    const pageReconciliationError = getPageReconciliationError(
      formBundle,
      mapped
    );
    if (pageReconciliationError) {
      reconciliationError = pageReconciliationError;
      return formBundle;
    }

    if (formBundle.id.startsWith(NEW_BUNDLE_ID_SENTINEL)) {
      needsIDSync = true;
      const reconciledBundle =
        formBundle.treeLabelSuffix === undefined
          ? mapped
          : { ...mapped, treeLabelSuffix: formBundle.treeLabelSuffix };
      return preserveCurrentEstateInsights(formBundle, reconciledBundle);
    }

    const hasNewPages = formBundle.pages.some((page) =>
      page.id.startsWith(NEW_PAGE_ID_SENTINEL)
    );
    const formEntitySummaryConfiguration =
      formBundle.entitySummaryConfiguration;
    const mappedEntitySummaryConfiguration = mapped.entitySummaryConfiguration;
    if (
      formEntitySummaryConfiguration &&
      !formEntitySummaryConfiguration.id &&
      (!mappedEntitySummaryConfiguration ||
        !entitySummaryConfigurationsMatch(
          formEntitySummaryConfiguration,
          mappedEntitySummaryConfiguration
        ))
    ) {
      reconciliationError = `response entity summary configuration does not match the replacement configuration for bundle ${formBundle.id}`;
      return formBundle;
    }

    const savedEntitySummaryConfiguration =
      formEntitySummaryConfiguration &&
      !formEntitySummaryConfiguration.id &&
      mappedEntitySummaryConfiguration?.id
        ? {
            ...formEntitySummaryConfiguration,
            id: mappedEntitySummaryConfiguration.id,
          }
        : null;
    if (hasNewPages || savedEntitySummaryConfiguration) {
      needsIDSync = true;
      return {
        ...formBundle,
        ...(savedEntitySummaryConfiguration
          ? { entitySummaryConfiguration: savedEntitySummaryConfiguration }
          : {}),
        pages: formBundle.pages.map((formPage, pageIndex) =>
          formPage.id.startsWith(NEW_PAGE_ID_SENTINEL)
            ? (mapped.pages[pageIndex] ?? formPage)
            : formPage
        ),
      };
    }

    const configIdChanged = mapped.pages.some((responsePage, pageIndex) => {
      const formPage = formBundle.pages[pageIndex];
      return (
        formPage?.balanceSheetBundleConfiguration &&
        responsePage.balanceSheetBundleConfiguration?.id &&
        formPage.balanceSheetBundleConfiguration.id !==
          responsePage.balanceSheetBundleConfiguration.id
      );
    });

    if (configIdChanged) {
      needsIDSync = true;
      return {
        ...formBundle,
        pages: formBundle.pages.map((formPage, pageIndex) => {
          const responsePage = mapped.pages[pageIndex];
          if (
            formPage.balanceSheetBundleConfiguration &&
            responsePage?.balanceSheetBundleConfiguration?.id
          ) {
            return {
              ...formPage,
              balanceSheetBundleConfiguration: {
                ...formPage.balanceSheetBundleConfiguration,
                id: responsePage.balanceSheetBundleConfiguration.id,
              },
            };
          }
          return formPage;
        }),
      };
    }

    return formBundle;
  });

  if (reconciliationError) {
    diagnostics.warn(`syncBundlesFromResponse: ${reconciliationError}`);
    return { kind: 'desynced', reason: reconciliationError };
  }

  if (!needsIDSync) return { kind: 'nothingToSync' };

  let newSelectedId: string | undefined;
  const remappedBundle = remapBundle(selectedItemId, bundles, updatedBundles);
  if (remappedBundle) {
    newSelectedId = isSinglePageBundle(remappedBundle.type)
      ? remappedBundle.id
      : (first(remappedBundle.pages)?.id ?? remappedBundle.id);
  } else {
    const remappedPage = remapPage(selectedItemId, bundles, updatedBundles);
    if (remappedPage && remappedPage.id !== selectedItemId) {
      newSelectedId = remappedPage.id;
    }
  }

  return { kind: 'synced', updatedBundles, newSelectedId };
}
